import { create } from 'zustand'
import { api } from '@/lib/api'
import type {
  AdoptionRow,
  ReviewBatchSummary,
  ReviewShootDetail,
  VariantFeedback,
  VariantReviewInput,
} from '@/types'

const REVIEWS_POLL_INTERVAL_MS = 30_000
const PRELOAD_PLAYBACK_WAIT_MS = 500

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export interface PreloadAssetInput {
  id: string
  url: string
  size: number | null
  mime: string | null
}

// 进度节流：累计 ≥1MB 才 flush 一次 store，避免每个网络 chunk 触发全页重渲
const PRELOAD_FLUSH_BYTES = 1_048_576

export type PreloadStatus = 'queued' | 'running' | 'done' | 'cancelled'

export interface PreloadProgress {
  status: PreloadStatus
  done: number
  failed: number
  total: number
  bytesDone: number
  bytesTotal: number // 已知 file_size 之和（估算值）
}

interface DailyReviewState {
  batches: ReviewBatchSummary[]
  batchesTotal: number
  // Current batch (per-product lazy loading)
  batch: ReviewBatchSummary | null
  adoption: AdoptionRow[]
  countryShoots: Record<string, ReviewShootDetail[]>
  loadingCountries: string[]
  isLoading: boolean
  error: string | null
  savingShootIds: Set<string>
  pollTimer: ReturnType<typeof setInterval> | null

  // 视频预加载（Blob 方案，仅 Video Daily Review 使用）。
  // 按市场（country）独立排队与统计进度；全局单 runner 串行执行，
  // 任意时刻只有一条下载流，保护共享出口带宽。
  videoBlobs: Record<string, string> // assetId -> blob URL
  preloads: Record<string, PreloadProgress> // country -> progress
  preloadQueue: { country: string; assets: PreloadAssetInput[] }[]
  preloadAborts: Record<string, AbortController>
  preloadRunnerActive: boolean
  activePlaybackCount: number // 有非 blob 视频在播放时预加载让路

  startPreload: (country: string, assets: PreloadAssetInput[]) => void
  cancelPreload: (country: string) => void
  releaseBlobs: () => void
  setPlaybackActive: (delta: 1 | -1) => void

  fetchBatches: () => Promise<void>
  /** Ensure batch summary (chips) is known; returns the country list. */
  initBatch: (batchId: string) => Promise<string[]>
  /** Fetch one product's shoots (cached after first load). */
  loadCountry: (batchId: string, country: string) => Promise<void>
  saveShootReview: (shootId: string, variants: Record<string, VariantReviewInput>) => Promise<void>
  startReviewsPolling: (batchId: string) => void
  stopReviewsPolling: () => void
  resetBatch: () => void
}

/**
 * 全局串行 runner：任意时刻只有一条下载流（保护共享出口带宽）。
 * 队列里每个 job 是一个市场；市场内逐个视频下载并更新该市场自己的进度。
 * 只会有一个 runner 实例在跑（preloadRunnerActive 守卫，JS 单线程下
 * 检查与置位之间无 await，不存在竞态窗口）。
 */
async function runPreloadQueue(
  get: () => DailyReviewState,
  set: (partial: Partial<DailyReviewState>) => void,
): Promise<void> {
  if (get().preloadRunnerActive) return
  set({ preloadRunnerActive: true })
  try {
    for (;;) {
      const queue = get().preloadQueue
      if (queue.length === 0) break
      const job = queue[0]
      set({ preloadQueue: queue.slice(1) })
      const progress = get().preloads[job.country]
      if (!progress || progress.status !== 'queued') continue // 排队期间被取消

      const abort = new AbortController()
      set({
        preloadAborts: { ...get().preloadAborts, [job.country]: abort },
        preloads: { ...get().preloads, [job.country]: { ...progress, status: 'running' } },
      })

      const patch = (update: Partial<PreloadProgress>) => {
        const current = get().preloads[job.country]
        if (!current) return // releaseBlobs 已清空
        set({ preloads: { ...get().preloads, [job.country]: { ...current, ...update } } })
      }

      for (const asset of job.assets) {
        if (abort.signal.aborted) break
        // 有视频正在从 S3 流式播放时让路（blob 播放不占网络，不让路）
        while (get().activePlaybackCount > 0 && !abort.signal.aborted) {
          await sleep(PRELOAD_PLAYBACK_WAIT_MS)
        }
        if (abort.signal.aborted) break
        try {
          // 同源后端代理需要带上 JWT（每个视频重读一次，兼容长预加载期间的 token 刷新）
          const token = localStorage.getItem('access_token')
          const response = await fetch(asset.url, {
            signal: abort.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          })
          if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`)
          const reader = response.body.getReader()
          const chunks: BlobPart[] = []
          let received = 0
          let flushed = 0
          for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            if (value) {
              chunks.push(value)
              received += value.length
              if (received - flushed >= PRELOAD_FLUSH_BYTES) {
                const delta = received - flushed
                flushed = received
                patch({ bytesDone: (get().preloads[job.country]?.bytesDone ?? 0) + delta })
              }
            }
          }
          // 取消/释放后绝不再写入（否则产生永不 revoke 的孤儿 blob）
          if (abort.signal.aborted) break
          const blobUrl = URL.createObjectURL(new Blob(chunks, { type: asset.mime ?? 'video/mp4' }))
          const current = get().preloads[job.country]
          set({
            videoBlobs: { ...get().videoBlobs, [asset.id]: blobUrl },
            ...(current
              ? {
                  preloads: {
                    ...get().preloads,
                    [job.country]: {
                      ...current,
                      done: current.done + 1,
                      bytesDone: current.bytesDone + (received - flushed),
                    },
                  },
                }
              : {}),
          })
        } catch {
          if (abort.signal.aborted) break
          // 单个失败不打断队列；播放兜底仍走流式 URL
          const current = get().preloads[job.country]
          if (current) patch({ failed: current.failed + 1 })
        }
      }

      // 收尾：正常完成 → done；取消路径状态已由 cancelPreload 标记
      const remainingAborts = { ...get().preloadAborts }
      delete remainingAborts[job.country]
      const finalProgress = get().preloads[job.country]
      set({
        preloadAborts: remainingAborts,
        ...(finalProgress && !abort.signal.aborted
          ? { preloads: { ...get().preloads, [job.country]: { ...finalProgress, status: 'done' } } }
          : {}),
      })
    }
  } finally {
    set({ preloadRunnerActive: false })
  }
}

export const useDailyReviewStore = create<DailyReviewState>((set, get) => ({
  batches: [],
  batchesTotal: 0,
  batch: null,
  adoption: [],
  countryShoots: {},
  loadingCountries: [],
  isLoading: false,
  error: null,
  savingShootIds: new Set(),
  pollTimer: null,

  videoBlobs: {},
  preloads: {},
  preloadQueue: [],
  preloadAborts: {},
  preloadRunnerActive: false,
  activePlaybackCount: 0,

  startPreload: (country: string, assets: PreloadAssetInput[]) => {
    const current = get().preloads[country]
    if (current?.status === 'queued' || current?.status === 'running') return
    const pending = assets.filter((a) => a.url && !get().videoBlobs[a.id])
    if (pending.length === 0) return

    set({
      preloads: {
        ...get().preloads,
        [country]: {
          status: 'queued',
          done: 0,
          failed: 0,
          total: pending.length,
          bytesDone: 0,
          bytesTotal: pending.reduce((acc, a) => acc + (a.size ?? 0), 0),
        },
      },
      preloadQueue: [...get().preloadQueue, { country, assets: pending }],
    })
    void runPreloadQueue(get, set)
  },

  cancelPreload: (country: string) => {
    const progress = get().preloads[country]
    if (!progress) return
    // 排队中：标记 cancelled，runner 取到时跳过；运行中：中断当前下载
    get().preloadAborts[country]?.abort()
    if (progress.status === 'queued' || progress.status === 'running') {
      set({
        preloads: { ...get().preloads, [country]: { ...progress, status: 'cancelled' } },
        preloadQueue: get().preloadQueue.filter((job) => job.country !== country),
      })
    }
  },

  releaseBlobs: () => {
    for (const abort of Object.values(get().preloadAborts)) {
      abort.abort()
    }
    for (const url of Object.values(get().videoBlobs)) {
      URL.revokeObjectURL(url)
    }
    set({ videoBlobs: {}, preloads: {}, preloadQueue: [], preloadAborts: {} })
  },

  setPlaybackActive: (delta: 1 | -1) => {
    set({ activePlaybackCount: Math.max(0, get().activePlaybackCount + delta) })
  },

  fetchBatches: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await api.listReviewBatches()
      set({ batches: data.items, batchesTotal: data.total, isLoading: false })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load batches', isLoading: false })
    }
  },

  initBatch: async (batchId: string) => {
    const existing = get().batch
    if (existing?.id !== batchId) {
      // 路由参数切换批次时组件不卸载：在此释放旧批次的 blob 并停掉其预加载
      get().releaseBlobs()
      set({ batch: null, adoption: [], countryShoots: {}, loadingCountries: [], error: null })
    }
    let summary = get().batches.find((b) => b.id === batchId) ?? (existing?.id === batchId ? existing : null)
    if (!summary) {
      const data = await api.listReviewBatches()
      set({ batches: data.items, batchesTotal: data.total })
      summary = data.items.find((b) => b.id === batchId) ?? null
    }
    if (!summary) {
      set({ error: 'Batch not found' })
      return []
    }
    set({ batch: summary })
    return Object.keys(summary.stats.countries ?? {}).sort()
  },

  loadCountry: async (batchId: string, country: string) => {
    const { countryShoots, loadingCountries } = get()
    if (countryShoots[country] || loadingCountries.includes(country)) return
    set({ loadingCountries: [...get().loadingCountries, country] })
    try {
      const data = await api.getReviewBatch(batchId, country)
      const group = data.countries.find((c) => c.country === country)
      set({
        batch: data.batch,
        adoption: data.adoption,
        countryShoots: { ...get().countryShoots, [country]: group?.shoots ?? [] },
      })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : `Failed to load ${country}` })
    } finally {
      set({ loadingCountries: get().loadingCountries.filter((c) => c !== country) })
    }
  },

  saveShootReview: async (shootId: string, variants: Record<string, VariantReviewInput>) => {
    const saving = new Set(get().savingShootIds)
    saving.add(shootId)
    set({ savingShootIds: saving })
    try {
      const result = await api.saveShootReview(shootId, variants)
      const myRows = result.reviews.filter((r) => r.reviewer_email)
      const myEmail = myRows[0]?.reviewer_email
      const countryShoots = Object.fromEntries(
        Object.entries(get().countryShoots).map(([country, shoots]) => [
          country,
          shoots.map((shoot) => {
            if (shoot.id !== shootId) return shoot
            const others = shoot.reviews.filter((r) => r.reviewer_email !== myEmail)
            return { ...shoot, reviews: [...others, ...myRows] }
          }),
        ])
      )
      set({ countryShoots })
    } finally {
      const done = new Set(get().savingShootIds)
      done.delete(shootId)
      set({ savingShootIds: done })
    }
  },

  startReviewsPolling: (batchId: string) => {
    get().stopReviewsPolling()
    const timer = setInterval(async () => {
      try {
        const payload = await api.getReviewBatchReviews(batchId)
        if (get().batch?.id !== batchId) return

        const byShoot = new Map<string, VariantFeedback[]>()
        for (const row of payload.reviews) {
          const list = byShoot.get(row.shoot_id) ?? []
          list.push(row)
          byShoot.set(row.shoot_id, list)
        }
        const countryShoots = Object.fromEntries(
          Object.entries(get().countryShoots).map(([country, shoots]) => [
            country,
            shoots.map((shoot) => ({ ...shoot, reviews: byShoot.get(shoot.id) ?? [] })),
          ])
        )
        // Adoption rows carry country/slug from the server payload
        const adoption: AdoptionRow[] = payload.reviews
          .filter((r) => r.rating === 'use' && r.variant > 0)
          .map((r) => ({
            shoot_id: r.shoot_id,
            country: r.country ?? '',
            slug: r.slug ?? '',
            variant: r.variant,
            reviewer_email: r.reviewer_email,
            post_processing_pct: r.post_processing_pct,
            changes: r.changes,
            note: r.note,
          }))
        set({ countryShoots, adoption })
      } catch {
        // polling errors are non-fatal; next tick retries
      }
    }, REVIEWS_POLL_INTERVAL_MS)
    set({ pollTimer: timer })
  },

  stopReviewsPolling: () => {
    const timer = get().pollTimer
    if (timer) {
      clearInterval(timer)
      set({ pollTimer: null })
    }
  },

  resetBatch: () => {
    get().stopReviewsPolling()
    get().releaseBlobs()
    set({ batch: null, adoption: [], countryShoots: {}, loadingCountries: [] })
  },
}))
